import { useState, useRef, useMemo, useEffect } from 'preact/hooks';
import { isAiChatDrawerOpenSignal, aiDrawerWidthSignal, setAiDrawerWidth } from '../../state/ui.store';
import { aiChatHistorySignal, isAiTypingSignal } from '../../state/ai.store';
import { activeContactSignal } from '../../state/contacts.store';
import { apiFetch } from '../../services/api';
import type { AiChatMessage } from '../../types/ai';
import { Bot, Maximize2, Minimize2, X, Send } from 'lucide-preact';
import { renderMarkdownToHtml } from '../../utils/markdownUtils';

function AssistantMessageContent({ content }: { content: string }) {
  const html = useMemo(() => renderMarkdownToHtml(content), [content]);

  return (
    <div
      class="markdown-body text-xs text-slate-200 leading-relaxed space-y-1.5 overflow-x-auto"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

export function ResizableDrawer() {
  const isOpen = isAiChatDrawerOpenSignal.value;
  const width = aiDrawerWidthSignal.value;
  const history = aiChatHistorySignal.value;
  const isTyping = isAiTypingSignal.value;
  const contact = activeContactSignal.value;

  const [prompt, setPrompt] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isMobile, setIsMobile] = useState<boolean>(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );

  const isResizingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(width);
  const historyContainerRef = useRef<HTMLDivElement>(null);
  const rafIdRef = useRef<number | null>(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-scroll to bottom when new messages arrive or typing status changes
  useEffect(() => {
    if (historyContainerRef.current) {
      historyContainerRef.current.scrollTop = historyContainerRef.current.scrollHeight;
    }
  }, [history.length, isTyping]);

  if (!isOpen) return null;

  const handlePointerDown = (e: PointerEvent) => {
    e.preventDefault();
    isResizingRef.current = true;
    setIsDragging(true);
    startXRef.current = e.clientX;
    startWidthRef.current = aiDrawerWidthSignal.value;

    const onPointerMove = (moveEvent: PointerEvent) => {
      if (!isResizingRef.current) return;
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);

      rafIdRef.current = requestAnimationFrame(() => {
        const delta = startXRef.current - moveEvent.clientX;
        setAiDrawerWidth(startWidthRef.current + delta);
      });
    };

    const onPointerUp = () => {
      isResizingRef.current = false;
      setIsDragging(false);
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  };

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    if (!prompt.trim() || !contact) return;

    const userMsgText = prompt.trim();
    setPrompt('');

    const userMsg: AiChatMessage = {
      id: Math.random().toString(36).substring(2, 9),
      role: 'user',
      content: userMsgText,
      timestamp: new Date().toISOString()
    };
    aiChatHistorySignal.value = [...aiChatHistorySignal.value, userMsg];
    isAiTypingSignal.value = true;

    try {
      const data = await apiFetch<{ response: string }>(`/ai/chat/${contact.phone_number}`, {
        method: 'POST',
        body: JSON.stringify({ prompt: userMsgText })
      });

      const assistantMsg: AiChatMessage = {
        id: Math.random().toString(36).substring(2, 9),
        role: 'assistant',
        content: data.response,
        timestamp: new Date().toISOString()
      };
      aiChatHistorySignal.value = [...aiChatHistorySignal.value, assistantMsg];
    } catch (err: any) {
      const errorMsg: AiChatMessage = {
        id: Math.random().toString(36).substring(2, 9),
        role: 'assistant',
        content: `[Erro ao obter resposta da IA]: ${err.message}`,
        timestamp: new Date().toISOString()
      };
      aiChatHistorySignal.value = [...aiChatHistorySignal.value, errorMsg];
    } finally {
      isAiTypingSignal.value = false;
    }
  };

  return (
    <div
      id="ai-chat-drawer"
      class={`resizable-drawer fixed inset-y-0 right-0 z-50 w-full md:relative md:inset-auto md:z-auto animate-fade-in flex-shrink-0 ${
        isDragging ? 'transition-none select-none' : 'transition-all duration-150'
      }`}
      style={isMobile ? {} : { width: `${Math.round(width)}px` }}
    >
      {/* Resizer Handle (Desktop Only) */}
      <div
        id="ai-chat-resizer"
        onPointerDown={handlePointerDown}
        onDblClick={() => setAiDrawerWidth(384)}
        class="drawer-resizer-handle hidden md:flex group hover:bg-cyan-500/20 transition-colors cursor-col-resize select-none touch-none"
        title="Arraste para alterar a largura (Duplo clique para resetar 384px)"
      >
        <div class="w-1.5 h-12 rounded-full bg-slate-700/80 group-hover:bg-cyan-400 group-hover:h-20 group-hover:w-2 transition-all shadow-lg" />
      </div>

      {/* Header */}
      <div class="drawer-header">
        <div class="flex items-center gap-2.5">
          <div class="w-7 h-7 rounded-lg bg-cyan-500/20 text-cyan-400 flex items-center justify-center font-bold">
            <Bot class="w-4 h-4 text-cyan-400" />
          </div>
          <div>
            <h3 class="text-xs font-bold text-white flex items-center gap-1.5">
              Conversa com Alfred
              <span class="text-[9px] font-mono font-semibold px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-300 border border-cyan-400/30">
                IA Ativa
              </span>
            </h3>
            <p class="text-[10px] text-slate-400">Assistente Multimodal RAG</p>
          </div>
        </div>
        <div class="flex items-center gap-1">
          {/* Preset Buttons for Easy Resizing */}
          <button
            onClick={() => setAiDrawerWidth(width > 550 ? 384 : 650)}
            class="hidden md:flex text-slate-400 hover:text-cyan-300 p-1.5 rounded-lg hover:bg-slate-800 transition cursor-pointer"
            title={width > 550 ? "Restaurar tamanho padrão (384px)" : "Expandir para 650px"}
          >
            {width > 550 ? <Minimize2 class="w-3.5 h-3.5" /> : <Maximize2 class="w-3.5 h-3.5" />}
          </button>
          <button
            onClick={() => isAiChatDrawerOpenSignal.value = false}
            class="text-slate-400 hover:text-rose-400 p-2 rounded-lg hover:bg-slate-800 transition cursor-pointer min-h-[44px] min-w-[44px] flex items-center justify-center"
            title="Fechar painel"
          >
            <X class="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Message History */}
      <div ref={historyContainerRef} class="drawer-history-container space-y-3.5 overflow-y-auto">
        {history.length === 0 ? (
          <div class="p-6 text-center text-xs text-slate-400 font-medium leading-relaxed">
            Pergunte qualquer coisa sobre o histórico de conversas deste contato para o assistente Alfred.
          </div>
        ) : (
          history.map(msg => (
            <div
              key={msg.id}
              data-ai-message="true"
              data-ai-role={msg.role}
              class={`p-3.5 rounded-2xl text-xs ${
                msg.role === 'user'
                  ? 'bg-cyan-950/60 border border-cyan-500/30 text-slate-100 ml-6 shadow-sm'
                  : 'bg-slate-950 border border-slate-800 text-slate-200 mr-4 shadow-sm'
              }`}
            >
              <span class="text-[9px] font-bold uppercase tracking-wider text-cyan-400 block mb-1.5">
                {msg.role === 'user' ? 'Você' : 'Alfred AI'}
              </span>
              {msg.role === 'assistant' ? (
                <AssistantMessageContent content={msg.content} />
              ) : (
                <p class="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
              )}
            </div>
          ))
        )}

        {isTyping && (
          <div class="p-3.5 rounded-2xl bg-slate-950 border border-slate-800 text-slate-400 text-xs italic animate-pulse flex items-center gap-2">
            <Bot class="w-4 h-4 text-cyan-400 animate-spin" />
            <span>Alfred está analisando as conversas...</span>
          </div>
        )}
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} class="drawer-form">
        <input
          type="text"
          placeholder="Pergunte sobre as conversas..."
          value={prompt}
          onInput={(e) => setPrompt((e.target as HTMLInputElement).value)}
          class="drawer-input placeholder-slate-500"
        />
        <button
          type="submit"
          class="px-4 py-2.5 bg-cyan-600 hover:bg-cyan-500 text-white rounded-xl font-bold transition cursor-pointer shadow-md shadow-cyan-600/20 flex items-center justify-center min-h-[44px] min-w-[44px]"
        >
          <Send class="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}
